import type { GoogleProtoDuration } from '@wix/metro-runtime/ambassador';

/** Schedule item describes the schedule within an event. Each event may contain multiple schedule items. */
export interface ScheduleItem {
  /**
   * Schedule item ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /** Whether a schedule item is hidden from guests. */
  hidden?: boolean;
  /**
   * Schedule item name.
   * @maxLength 120
   */
  name?: string;
  /** Time slot of a schedule item. */
  timeSlot?: TimeInterval;
  /**
   * Rich text schedule item description.
   * @maxLength 10000
   */
  description?: string;
  /**
   * Stage or room name in which a session takes place.
   * @maxLength 30
   */
  stageName?: string;
  /**
   * Schedule item tags. Used to organize the items by a theme.
   * @maxSize 5
   * @minLength 1
   * @maxLength 30
   */
  tags?: string[];
  /** Schedule item status. */
  status?: ScheduleStatusWithLiterals;
  /**
   * Date and time the schedule item was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the schedule item was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Event ID to which the schedule item belongs.
   * @format GUID
   * @readonly
   */
  eventId?: string;
  /**
   * Whether the schedule item is a draft.
   * @readonly
   */
  draft?: boolean;
}

/** Time interval on the timeline between two points in time. */
export interface TimeInterval {
  /** Start of the time slot interval, inclusive. */
  start?: Date | null;
  /** End of the time slot interval, exclusive. */
  end?: Date | null;
  /**
   * Time zone ID in TZ database format, e.g., `"EST"`, `"America/Los_Angeles"`.
   * Defaults to `"Etc/UTC"` when not set.
   * @minLength 1
   * @maxLength 100
   */
  timeZoneId?: string | null;
}

export enum ScheduleStatus {
  /** Item is scheduled for a future date. */
  SCHEDULED = 'SCHEDULED',
  /** Item is canceled. */
  CANCELED = 'CANCELED',
}

/** @enumType */
export type ScheduleStatusWithLiterals =
  | ScheduleStatus
  | 'SCHEDULED'
  | 'CANCELED';

export interface ListScheduleItemsRequest {
  /**
   * Event ID to which the schedule items belong.
   * @format GUID
   * @maxSize 100
   */
  eventId?: string[];
  /**
   * Schedule item state filter.
   *
   * Defaults to `["PUBLISHED", "VISIBLE"]` when no filters are specified. <br/>
   * If neither `"PUBLISHED"` nor `"DRAFT"` are specified, assumes `"PUBLISHED"`, for example: [`"HIDDEN"`] becomes [`"HIDDEN"`, `"PUBLISHED"`].
   * If neither `"VISIBLE"` nor `"HIDDEN"` are specified, assumes `"VISIBLE"`, for example: [`"DRAFT"`] becomes [`"DRAFT"`, `"VISIBLE"`].
   * @maxSize 20
   */
  state?: StateFilterWithLiterals[];
  /** Filters schedule items starting on or after specified point in time. Inclusive. */
  startingFrom?: Date | null;
  /** Filters schedule items starting before specified point in time. Non-inclusive. */
  startingBefore?: Date | null;
  /**
   * *Deprecated:** Use `paging` instead.
   * @max 1000
   * @deprecated
   */
  offset?: number;
  /**
   * *Deprecated:** Use `paging` instead.
   * @max 100
   * @deprecated
   */
  limit?: number;
  /**
   * Filter facets.
   * @minLength 1
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
  /**
   * Item IDs filter.
   * @format GUID
   * @maxSize 100
   */
  itemId?: string[];
  /**
   * Tags filter.
   * @minLength 1
   * @maxLength 30
   * @maxSize 50
   */
  tag?: string[];
  /**
   * Stage names filter.
   * @maxLength 30
   * @maxSize 50
   */
  stageName?: string[];
  /**
   * Pointer to page of results using offset.
   * See [Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging#paging).
   */
  paging?: Paging;
}

export enum StateFilter {
  /** Schedule item is published. */
  PUBLISHED = 'PUBLISHED',
  /** Schedule item is in draft. */
  DRAFT = 'DRAFT',
  /** Schedule item is visible to the event's guests. */
  VISIBLE = 'VISIBLE',
  /** Schedule item is hidden to the event's guests. */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type StateFilterWithLiterals =
  | StateFilter
  | 'PUBLISHED'
  | 'DRAFT'
  | 'VISIBLE'
  | 'HIDDEN';

export interface Paging {
  /**
   * Number of items to load per page.
   * @max 1000
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface ListScheduleItemsResponse {
  /**
   * *Deprecated:** Use `pagingMetadata.total` instead.
   * @readonly
   * @deprecated
   */
  total?: number;
  /**
   * *Deprecated.**
   * @deprecated
   */
  limit?: number;
  /**
   * *Deprecated:** Use `pagingMetadata.offset` instead.
   * @deprecated
   */
  offset?: number;
  /** Schedule items. */
  items?: ScheduleItem[];
  /**
   * Facets.
   * @readonly
   */
  facets?: Record<string, FacetCounts>;
  /**
   * Whether there are draft changes which have not been published.
   * @readonly
   */
  draftNotPublished?: boolean | null;
  /** Paging metadata. */
  pagingMetadata?: PagingMetadataV2;
}

export interface FacetCounts {
  /** Facet counts aggregated per value. */
  counts?: Record<string, number>;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface QueryScheduleItemsRequest {
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /**
   * Pointer to page of results using offset.
   * See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).
   */
  paging?: Paging;
  /** Filter. See [supported fields and operators](https://dev.wix.com/api/rest/wix-events/wix-events/filter-and-sort#wix-events_wix-events_filter-and-sort_list-query-events). */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the form [{"fieldName":"sortField1"},{"fieldName":"sortField2","direction":"DESC"}]
   * See [supported fields](https://dev.wix.com/api/rest/wix-events/wix-events/filter-and-sort#wix-events_wix-events_filter-and-sort_list-query-events).
   * @maxSize 20
   */
  sort?: Sorting[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /**
   * Pointer to page of results using offset.
   * See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).
   */
  paging?: Paging;
}

export interface Sorting {
  /**
   * Name of the field to sort by
   * @maxLength 100
   */
  fieldName?: string;
  /** Sort order (ASC/DESC). Defaults to ASC */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface QueryScheduleItemsResponse {
  pagingMetadata?: PagingMetadataV2;
  /** Schedule items. */
  items?: ScheduleItem[];
}

export interface GetScheduleItemRequest {
  /** Event ID to which the schedule item belongs. */
  eventId?: string;
  /**
   * Schedule item ID.
   * @format GUID
   */
  itemId: string;
  /** Whether to include draft schedule items in the response. */
  includeDraft?: boolean;
}

export interface GetScheduleItemResponse {
  /** Schedule item. */
  item?: ScheduleItem;
  /** Draft of the schedule item. */
  draft?: ScheduleItem;
}

export interface AddScheduleItemRequest {
  /**
   * Event ID to add the schedule item to.
   * @format GUID
   */
  eventId: string;
  /** Schedule item. */
  item?: ScheduleItemData;
}

/** Schedule item describes the schedule within an event. Each event may contain multiple schedule items. */
export interface ScheduleItemData {
  /** Whether the schedule item is hidden from guests. */
  hidden?: boolean;
  /**
   * Schedule item name.
   * @maxLength 120
   */
  name?: string;
  /** Time slot of a schedule item. */
  timeSlot?: TimeInterval;
  /**
   * Rich text schedule item description.
   * @maxLength 10000
   */
  description?: string;
  /**
   * Stage or room name in which a session takes place.
   * @maxLength 30
   */
  stageName?: string;
  /**
   * Schedule item tags. Used to organize the items by a theme.
   * @maxSize 5
   * @minLength 1
   * @maxLength 30
   */
  tags?: string[];
  /** Schedule item status. */
  status?: ScheduleStatusWithLiterals;
}

export interface AddScheduleItemResponse {
  /** Schedule item. */
  item?: ScheduleItem;
}

export interface UpdateScheduleItemRequest {
  /**
   * Event ID to which the schedule item belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Schedule item ID to update.
   * @format GUID
   */
  itemId: string;
  /** Schedule item info. */
  item?: ScheduleItemData;
  /**
   * Set of field paths to update.
   * When fields are empty, request is interpreted as full update.
   * Behavior follows [google.protobuf.FieldMask](https://developers.google.com/protocol-buffers/docs/reference/google.protobuf#google.protobuf.FieldMask) semantics.
   */
  fields?: string[];
}

export interface UpdateScheduleItemResponse {
  /** Schedule item. */
  item?: ScheduleItem;
}

export interface DeleteScheduleItemRequest {
  /**
   * Event ID to which the schedule item belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Schedule items to delete.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  itemIds?: string[];
}

export interface DeleteScheduleItemResponse {}

export interface DiscardDraftRequest {
  /**
   * Event ID to which the draft schedule item belongs.
   * @format GUID
   */
  eventId: string;
}

export interface DiscardDraftResponse {}

export interface PublishDraftRequest {
  /**
   * Event ID to which the draft schedule item belongs.
   * @format GUID
   */
  eventId: string;
}

export interface PublishDraftResponse {}

export interface RescheduleDraftRequest {
  /**
   * Event ID to which the draft schedule item belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Time zone ID in TZ database format, e.g., `"EST"`, `"America/Los_Angeles"`.
   * @maxLength 100
   */
  timeZoneId: string;
  /** Offset added or subtracted from schedule item start & end times. */
  timeSlotOffset?: GoogleProtoDuration;
}

export interface RescheduleDraftResponse {}

export interface ListBookmarksRequest {
  /**
   * Event ID to which the schedule item belongs.
   * @format GUID
   */
  eventId?: string;
}

export interface ListBookmarksResponse {
  /** Schedule items. */
  items?: ScheduleItem[];
}

export interface CreateBookmarkRequest {
  /**
   * Event ID to which the schedule item belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * Schedule item ID.
   * @format GUID
   */
  itemId?: string;
}

export interface CreateBookmarkResponse {}

export interface DeleteBookmarkRequest {
  /**
   * Event ID to which the schedule item belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * Schedule item ID.
   * @format GUID
   */
  itemId?: string;
}

export interface DeleteBookmarkResponse {}

/** @docsIgnore */
export type QueryScheduleItemsApplicationErrors = {
  code?: 'FILTER_PARSER_ERROR';
  description?: string;
  data?: Record<string, any>;
};
